/**
 * AuthButtons Component
 * 
 * A molecule component that displays login and register buttons for authentication.
 * This component is platform-agnostic and accepts label text and analytics callbacks via props.
 * 
 * @module @ideoz/ui/components/molecules/AuthButtons
 */

import React from 'react';
import { Button } from '../atoms/button';
import type { AuthButtonsProps } from '../../types/AuthButtons.types';

/**
 * AuthButtons Component
 * 
 * Displays login and register buttons for unauthenticated users.
 * Supports custom labels, analytics callbacks, and loading/disabled states.
 * 
 * @component
 * @example
 * ```tsx
 * // Basic usage
 * <AuthButtons 
 *   onLogin={() => navigate('/login')} 
 *   onRegister={() => navigate('/register')}
 * />
 * 
 * // With custom labels
 * <AuthButtons 
 *   onLogin={handleLogin} 
 *   onRegister={handleRegister}
 *   loginLabel="Sign In"
 *   registerLabel="Sign Up"
 * />
 * 
 * // With analytics
 * <AuthButtons 
 *   onLogin={handleLogin} 
 *   onRegister={handleRegister}
 *   onLoginAnalytics={() => trackEvent('login_click')}
 *   onRegisterAnalytics={() => trackEvent('register_click')}
 * />
 * ```
 */
export const AuthButtons: React.FC<AuthButtonsProps> = ({
  onLogin,
  onRegister,
  loginLabel = 'Login',
  registerLabel = 'Create account free',
  onLoginAnalytics,
  onRegisterAnalytics,
  disabled = false,
  isLoading = false,
  className = '',
  testId = 'auth-buttons',
}) => {
  const handleLoginClick = () => {
    if (onLoginAnalytics) {
      onLoginAnalytics();
    }
    onLogin();
  };

  const handleRegisterClick = () => {
    if (onRegisterAnalytics) {
      onRegisterAnalytics();
    }
    onRegister();
  };

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      data-testid={testId}
    >
      <Button
        variant="secondary-gray"
        onClick={handleLoginClick}
        disabled={disabled || isLoading}
        data-testid={`${testId}-login`}
        aria-label={loginLabel}
         size="sm"
      >
        {loginLabel}
      </Button>
      <Button
        variant="primary"
        onClick={handleRegisterClick}
        disabled={disabled || isLoading}
        data-testid={`${testId}-register`}
        aria-label={registerLabel}
        size="sm"
      >
        {registerLabel}
      </Button>
    </div>
  );
};

AuthButtons.displayName = 'AuthButtons';
